import React, { useMemo, useState } from 'react';

import { useQueries } from '@tanstack/react-query';

import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Link as MuiLink,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { useGetCustomerServiceRepAssignments } from '~/hooks/apis/loyalty/useCustomerServiceRepAssignments';
import { useGetCustomerServiceReps } from '~/hooks/apis/loyalty/useCustomerServiceReps';

import { Link } from '~/lib/react-router-dom';

import { formatDateSlashes } from '~/services/TimeService';
import UsersService from '~/services/UsersService';

import type { CustomerServiceRep } from '~/services/LuxLoyaltyCustomerServiceRepService';

import AssignmentReassignDialog from './AssignmentReassignDialog';

const DEFAULT_ASSIGNMENTS_PAGE_SIZE = 25;
const ASSIGNMENTS_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

interface ReassignTarget {
  customerId: string;
  memberLabel: string;
  currentRepId: string | null;
}

function initialsFromName(name: string | null | undefined): string {
  if (!name) {
    return '?';
  }
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return `${first}${last}`.toUpperCase() || '?';
}

interface CustomerSummary {
  fullName: string | null;
  email: string | null;
}

/** Assignments tab: list members with their assigned rep + per-row reassign. */
export default function AssignmentsTab() {
  const [repFilter, setRepFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_ASSIGNMENTS_PAGE_SIZE);

  const repsQuery = useGetCustomerServiceReps({ page: 1, pageSize: 50 });
  const assignmentsQuery = useGetCustomerServiceRepAssignments({
    customerServiceRepId: repFilter === 'all' ? undefined : repFilter,
    page,
    pageSize,
  });

  const reps = repsQuery.data?.result.reps ?? [];
  const assignments = assignmentsQuery.data?.result.accounts ?? [];
  const totalRecords = assignmentsQuery.data?.result.pageInfo.totalRecords ?? 0;

  // Fetch each row's customer in parallel. Shared 'users' query key so other
  // pages can hit the cache and vice-versa.
  const customerQueries = useQueries({
    queries: assignments.map((row) => ({
      queryKey: ['users', row.customerId] as const,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        UsersService.getUser(row.customerId, { signal }) as Promise<App.User>,
      staleTime: 60_000,
    })),
  });

  const customersById = useMemo(() => {
    const map: Record<string, CustomerSummary> = {};
    assignments.forEach((row, idx) => {
      const user = customerQueries[idx]?.data as App.User | undefined;
      map[row.customerId] = {
        fullName: user?.fullName ?? null,
        email: user?.email ?? null,
      };
    });
    return map;
  }, [assignments, customerQueries]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) {
      return assignments;
    }
    return assignments.filter((row) => {
      if (row.customerId.toLowerCase().includes(needle)) {
        return true;
      }
      if (row.ibsMemberId?.toLowerCase().includes(needle)) {
        return true;
      }
      const customer = customersById[row.customerId];
      if (customer?.fullName?.toLowerCase().includes(needle)) {
        return true;
      }
      if (customer?.email?.toLowerCase().includes(needle)) {
        return true;
      }
      if (row.customerServiceRep) {
        const rep = row.customerServiceRep;
        if (`${rep.firstName} ${rep.lastName}`.toLowerCase().includes(needle)) {
          return true;
        }
        if (rep.email.toLowerCase().includes(needle) || rep.phone.toLowerCase().includes(needle)) {
          return true;
        }
      }
      return false;
    });
  }, [assignments, customersById, search]);

  const [reassignTarget, setReassignTarget] = useState<ReassignTarget | null>(null);

  return (
    <Box>
      <Stack mb={3} spacing={1}>
        <Typography variant="h6">Assignments</Typography>
        <Typography variant="body2" color="text.secondary">
          Assign a dedicated concierge representative to every Infinite member. Assignments drive the concierge details
          on each member&apos;s Infinite landing page.
        </Typography>
      </Stack>

      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        Changes publish to each member&apos;s Infinite landing page automatically — concierge <strong>name</strong>,{' '}
        <strong>mobile</strong> and <strong>email</strong> update on save.
      </Alert>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by member, email, customer ID or phone"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <TextField
          select
          size="small"
          label="Rep"
          value={repFilter}
          onChange={(e) => {
            setRepFilter(e.target.value);
            setPage(1);
          }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="all">All representatives</MenuItem>
          {reps.map((rep) => (
            <MenuItem key={rep.id} value={rep.id}>
              {rep.firstName} {rep.lastName}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Stack direction="row" justifyContent="flex-end" mb={1}>
        <Typography variant="caption" color="text.secondary">
          {search.trim()
            ? `${filtered.length} of ${assignments.length} on this page · ${totalRecords} total`
            : `${totalRecords} member${totalRecords === 1 ? '' : 's'}`}
        </Typography>
      </Stack>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={(theme) => ({
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          overflow: 'hidden',
          '& thead .MuiTableCell-root': {
            bgcolor: theme.palette.action.hover,
            fontWeight: 700,
            color: 'text.secondary',
            textTransform: 'uppercase',
            fontSize: 12,
            letterSpacing: 0.6,
          },
        })}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Member</TableCell>
              <TableCell>Customer ID</TableCell>
              <TableCell>Assigned representative</TableCell>
              <TableCell>Last updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assignmentsQuery.isLoading && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            )}
            {assignmentsQuery.isError && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Alert severity="error">Failed to load assignments.</Alert>
                </TableCell>
              </TableRow>
            )}
            {!assignmentsQuery.isLoading && !assignmentsQuery.isError && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>
                    No assignments match the current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((row) => {
              const rep: CustomerServiceRep | null = row.customerServiceRep;
              const customer = customersById[row.customerId];
              const customerName = customer?.fullName ?? null;
              const memberLabel = customerName ?? `Member #${row.accountNumber}`;
              return (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        sx={(theme) => ({
                          width: 32,
                          height: 32,
                          fontSize: 13,
                          fontWeight: 700,
                          background: customerName
                            ? theme.palette.loyaltyTiers.infinite.gradient
                            : theme.palette.action.disabledBackground,
                          color: customerName ? 'common.white' : 'text.disabled',
                        })}
                      >
                        {initialsFromName(customerName)}
                      </Avatar>
                      <Box>
                        {customerName ? (
                          <MuiLink
                            component={Link}
                            to={`/users/${row.customerId}`}
                            variant="body2"
                            fontWeight={600}
                            underline="hover"
                            color="text.primary"
                          >
                            {customerName}
                          </MuiLink>
                        ) : (
                          <Typography variant="body2" fontWeight={600} component="em" sx={{ opacity: 0.7 }}>
                            Loading…
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" component="div">
                          {customer?.email ?? '—'}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <MuiLink
                      component={Link}
                      to={`/users/${row.customerId}`}
                      variant="caption"
                      underline="hover"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {row.customerId}
                    </MuiLink>
                  </TableCell>
                  <TableCell>
                    {rep ? (
                      <Stack>
                        <Typography variant="body2" fontWeight={600}>
                          {rep.firstName} {rep.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {rep.email} · {rep.phone}
                        </Typography>
                      </Stack>
                    ) : (
                      <Chip size="small" color="warning" label="Unassigned" />
                    )}
                  </TableCell>
                  <TableCell>{formatDateSlashes(row.updatedAt)}</TableCell>
                  <TableCell align="right">
                    <MuiLink
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() =>
                        setReassignTarget({
                          customerId: row.customerId,
                          memberLabel,
                          currentRepId: rep?.id ?? null,
                        })
                      }
                      disabled={reps.length === 0}
                    >
                      Reassign
                    </MuiLink>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalRecords}
        page={page - 1}
        onPageChange={(_, nextPage) => setPage(nextPage + 1)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => {
          setPageSize(Number(e.target.value));
          setPage(1);
        }}
        rowsPerPageOptions={[...ASSIGNMENTS_PAGE_SIZE_OPTIONS]}
        sx={{ borderTop: 1, borderColor: 'divider', mt: 0.5 }}
      />

      <AssignmentReassignDialog
        open={!!reassignTarget}
        onClose={() => setReassignTarget(null)}
        customerId={reassignTarget?.customerId ?? ''}
        memberLabel={reassignTarget?.memberLabel ?? ''}
        currentRepId={reassignTarget?.currentRepId ?? null}
        reps={reps}
      />
    </Box>
  );
}
